import { Grid } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { AppBox, AppButton, AppTypography, TextWithToolTip } from 'components'
import { ApiQueryKeys, appRoutes, UserStatuses } from 'globals/constants'
import { useSnackbar } from 'notistack'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ChangeMembershipResponse, MembershipApi, SessionModel, UserApi } from 'sdk'
import { useAppDispatch } from 'store/app.store'
import { AuthSliceSelector, setMemberships, setSession } from 'store/slices'
import { setIsProfileLoaderVisible } from 'store/slices/common.slice'

interface Props {}

export const PaymentDeclinedUserBillingInfoCard: FC<Props> = () => {
  const membershipApi = new MembershipApi()
  const userApi = new UserApi()
  const { enqueueSnackbar } = useSnackbar()
  const { CurrentUser } = useSelector(AuthSliceSelector)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [checkPaymentTimer, setCheckPaymentTimer] = useState<boolean>(false)
  const { mutateAsync: getMemberships } = useMutation({
    mutationKey: [ApiQueryKeys.memberships.GetUserActiveMembershipPlans],
    mutationFn: () => membershipApi.GetUserActiveMemberships(),
    onError: () => {},
  })

  const { mutate: getStatus } = useMutation({
    mutationKey: [ApiQueryKeys.user.getUserStatus],
    mutationFn: () => userApi.getUserStatus(),
    onError: () => {
      dispatch(setIsProfileLoaderVisible(false))
    },
    onSuccess: (data) => {
      if (data === UserStatuses.PAYMENT_DECLINED) {
        enqueueSnackbar(`We could not proceed with your payment.`, { variant: 'info' })
      } else if (data === UserStatuses.COMPLETED || data === UserStatuses.NO_PERSONAL_INFO) {
        getMemberships()
          .then((x) => {
            dispatch(setIsProfileLoaderVisible(false))
            enqueueSnackbar('Payment successful', { variant: 'success' })
            dispatch(setMemberships(x))
            const session = JSON.parse(JSON.stringify(CurrentUser)) as SessionModel
            session.Status = data
            dispatch(setSession(session))
            navigate(appRoutes.account.path)
          })
          .catch(() => {})
        return
      } else {
        dispatch(setIsProfileLoaderVisible(false))
      }
    },
  })

  const { mutate: retry, isPending: isRetrying } = useMutation({
    mutationKey: [ApiQueryKeys.memberships.RetryPayment],
    mutationFn: () => membershipApi.RetryFailedMembershipPayment(),
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    onSuccess: (data: ChangeMembershipResponse) => {
      if (data) {
        enqueueSnackbar(`Please allow up to two minutes to process your payment.`, { variant: 'info' })
        dispatch(setIsProfileLoaderVisible(true))
        setCheckPaymentTimer(true)
      }
    },
  })

  useEffect(() => {
    if (!checkPaymentTimer) {
      return
    }
    const timeout = setTimeout(() => getStatus(), 5000)
    return () => clearTimeout(timeout)
  }, [checkPaymentTimer])
  return (
    <AppBox border={2} borderColor={'red'} borderRadius={6} padding={2} backgroundColor="white">
      <AppBox display={'flex'} flexDirection={'column'} gap={2}>
        <AppBox paddingBottom={1} borderBottom={1} borderColor={'grey.100'}>
          <TextWithToolTip
            tooltipText={
              <AppTypography>
                As the <b>Account Owner</b> you are the only person that can make changes to your plan and add users."
              </AppTypography>
            }
          >
            <AppTypography color={'grey.900'} fontSize={16} fontWeight={700}>
              Account Owner
            </AppTypography>
          </TextWithToolTip>
        </AppBox>
        <Grid container paddingBottom={2}>
          <Grid item xs={7}>
            <AppTypography fontSize={14} color={'grey.500'}>
              The charges to your payment method have been declined. Please provide a new payment method within the
              <b>Payment Method</b> section below and click <b>Retry Payment</b>. All sub-accounts will be required to
              be to re-added to your billing using the <b>Actions</b> menu within your <b>Manage Users</b> section.
            </AppTypography>
          </Grid>
          <Grid item xs={5} justifyContent={'center'} textAlign={'center'}>
            <AppButton variant="contained" sidePaddings={4} isLoading={isRetrying} onClick={() => retry()}>
              Retry Payment
            </AppButton>
          </Grid>
        </Grid>
      </AppBox>
    </AppBox>
  )
}
